import type { AccountFormValuesV2 } from 'views/Assistant/components/AccountForm';

const strings = {
  login_account: ['account', 16],
  password: ['password', 20],
  confirm_password: ['confirmPassword', 20],
  display_name: ['name', 20],
  email: ['email', 320],
  phone_number: ['phoneNumber', 64],
} as const;
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const integer = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;

/** Match the Perseus wire fields while retaining Fett's existing form and validation. */
export function parseAccountCreateParams(raw: unknown): Partial<AccountFormValuesV2> | undefined {
  if (
    !record(raw) ||
    Object.keys(raw).some(
      (key) =>
        ![
          ...Object.keys(strings),
          'incoming_line_limit',
          'time_zone',
          'reception_limit_mode',
          'role_selections',
        ].includes(key),
    )
  )
    return;
  const values: Partial<AccountFormValuesV2> = {};
  for (const key of Object.keys(strings) as (keyof typeof strings)[]) {
    if (!(key in raw)) continue;
    const [field, limit] = strings[key];
    const value = raw[key];
    if (typeof value !== 'string' || value.length > limit) return;
    values[field] = value;
  }
  if ('incoming_line_limit' in raw) {
    if (!integer(raw.incoming_line_limit, 0, 1000)) return;
    values.incomingLineLimit = raw.incoming_line_limit;
  }
  if ('time_zone' in raw) {
    if (!integer(raw.time_zone, -12, 12)) return;
    values.timeZone = raw.time_zone;
  }
  if ('reception_limit_mode' in raw) {
    if (!['limited', 'unlimited'].includes(raw.reception_limit_mode as string)) return;
    values.type = raw.reception_limit_mode === 'limited' ? 2 : 1;
  }
  if ('role_selections' in raw) {
    if (!Array.isArray(raw.role_selections)) return;
    values.roleInDepartments = [];
    for (const role of raw.role_selections) {
      if (
        !record(role) ||
        Object.keys(role).some((key) => !['department_id', 'role_id'].includes(key)) ||
        !integer(role.department_id, 1, Number.MAX_SAFE_INTEGER) ||
        ('role_id' in role && !integer(role.role_id, 1, Number.MAX_SAFE_INTEGER))
      )
        return;
      values.roleInDepartments.push({
        departmentId: role.department_id,
        ...('role_id' in role ? { roleId: role.role_id as number } : {}),
      });
    }
  }
  return values;
}
